import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import IntegrationUsersService from "../../../../services/web/integration-users-service";
import { Template } from "../../../../components/template/template";
import Button from "../../../../components/button/button";
import { FaPlus } from "react-icons/fa";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import SelectComponent from "../../../../components/select/select-component";
import InputSwitch from "../../../../components/input/input-switch";
import { useParams } from "react-router";
import AlertMessages from "../../../../components/messages/alert-message";
import ProjectUtils from "../../../../utils/project-utils";
import { navigateHook } from "../../../../hooks/use-navigate";
import {toast} from "react-toastify";

const initialUser = {
    id: "",
    status: true,
    name: "",
    nameWar: "",
    token: "",
    login: "",
};

export default function IntegrationUsersForm() {
    const navigate = navigateHook();
    const [user, setUser] = useState(initialUser);
    const [rolesSelected, setRolesSelected] = useState([]);
    const { id } = useParams();
    const isEdit = !!id;
    
    const handleInputs = ({target: {value, id}}) => setUser(prev => ({...prev, [id]: value}));
    
    const {data: userInfo, isLoading: isLoadingForm ,refetch} = useQuery({
        queryKey: ["user-get-info"],
        queryFn: IntegrationUsersService.getInfo,
    });

    const userQuery = useQuery({
        queryKey: ["integration-user", id],
        queryFn: () => IntegrationUsersService.getUser(id),
        enabled: !!id,
    });

    useEffect(() => {
        if (userQuery.data) setUser(userQuery.data);
    }, [userQuery.data]);

    useEffect(() => {
        if (!userInfo?.roles || !user?.roles) return;
        setRolesSelected(userInfo.roles.filter(r => user.roles.includes(parseInt(r.value))));
    }, [userInfo, user]);

    const getId = (res) => res?.id ?? res?.data?.id ?? res?.data ?? res;

    const getIdByLogin = async (login) => {
        const all = await IntegrationUsersService.list();
        return (all ?? []).find((u) => u?.login === login)?.id;
    };

    const handleSave = useMutation({
        mutationFn: async () => {
            const preparedData = { ...user, roles: rolesSelected.map(r => r.value) };

            const formData = new FormData();
            formData.append("data", JSON.stringify(preparedData));

            return IntegrationUsersService.save(formData);
        },
        onSuccess: async (res) => {
            ProjectUtils.success(!isEdit);
            
            if (isEdit) {
                userQuery.refetch?.();
                return;
            }

            let newId = getId(res);
            if (!newId) newId = await getIdByLogin(user.login);

            if (!newId) {
                return;
            };

            const urlDev = `/integration-users/form/${newId}`;
            navigate(urlDev);
        },
        onError: (err) => {
            const message = err?.response?.data?.message;
            const msg = AlertMessages?.[message] ?? "Não foi possível salvar.";
            ProjectUtils.swalErrorMessage("", msg, "Ok");
        },
    });

    const isLoading = isLoadingForm || (isEdit && userQuery.isLoading) || handleSave.isPending;

    const regenToken = async () => {
        const res = await IntegrationUsersService.genToken(user.id);

        const newToken = res?.token ?? res?.data?.token ?? res;

        if (newToken && typeof newToken === "string") {
            setUser((prev) => ({ ...prev, token: newToken }));
            toast.success("Token gerado com sucesso");
            return;
        };

    };

    const copyToken = () => {
        ProjectUtils.copyToClipboard(user.token);
        toast.success("Token copiado com sucesso");
    };

    const newUser = () => {
        setUser(initialUser);
        setRolesSelected([]);
        const urlDev = `/integration-users/form`;
        navigate(urlDev);
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Usuário de integração</Template.Title>
                {isEdit && user?.id && (
                    <Button target="primary-outline" onClick={newUser}><FaPlus />Adicionar usuário</Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <InputSwitch 
                                    id="status" 
                                    label="Usuário ativo"
                                    checked={!!user.status}
                                    onChange={(checked) => setUser(prev => ({ ...prev, status: checked }))}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 mt-4">
                            <div className="flex-1">
                                <InputForm 
                                    id="name"
                                    label="Nome"
                                    value={user.name}
                                    onChange={handleInputs}
                                />
                            </div>
                            <div className="flex-1">
                                <InputForm
                                    id="nameWar"
                                    label="Nome de guerra"
                                    value={user.nameWar}
                                    onChange={handleInputs}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 mt-4">
                            <div className="flex-1">
                                <InputForm 
                                    id="login"
                                    label="Login"
                                    value={user.login}
                                    onChange={handleInputs}
                                />
                            </div>
                            <div className="flex-1">
                                <SelectComponent 
                                    label="Funções"
                                    value={rolesSelected}
                                    options={userInfo?.roles ?? []}
                                    onChange={(value) => {
                                        setRolesSelected(value)
                                    }}
                                    isMulti
                                />
                            </div>
                        </div>
                        {isEdit && user?.id && (
                            <div className="flex pt-4 gap-2">
                                <InputForm
                                    label="token"
                                    value={user.token}
                                    disabled={true}
                                    classNameWrapper="flex-1"
                                />
                                <Button onClick={copyToken} target="secondary">
                                    Copiar token
                                </Button>

                                <Button
                                    onClick={regenToken}
                                >
                                    Gerar novo token
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        <Button onClick={() => handleSave.mutate()} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}